import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { 
  UserPlus, 
  Mail, 
  Shield, 
  AlertCircle, 
  CheckCircle2,
  Eye,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteReviewerModal({ 
  isOpen, 
  onClose, 
  pursuitId, 
  pursuitName,
  onInviteSuccess 
}) {
  const { getAuthHeaders } = useAuth();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/permissions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            access_role: 'REVIEWER',
            message: message.trim() || null
          })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to invite reviewer');
      }
      
      setSuccess(`Successfully invited ${data.permission?.user_email || email}`);
      toast.success(`Reviewer access granted to ${data.permission?.user_email || email}`);
      
      // Reset form
      setEmail('');
      setMessage('');
      
      // Callback
      if (onInviteSuccess) {
        onInviteSuccess(data);
      }
      
      // Close after a brief delay
      setTimeout(() => {
        onClose();
        setSuccess(null);
      }, 2000);
      
    } catch (err) {
      console.error('Error inviting reviewer:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setEmail('');
    setMessage('');
    setError(null);
    setSuccess(null);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="invite-reviewer-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle>Invite Reviewer</DialogTitle>
              <DialogDescription className="text-xs">
                Grant read-only access to this pursuit
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          {/* Pursuit Info */}
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Granting access to:</p>
            <p className="font-medium text-slate-900 text-sm">{pursuitName}</p>
          </div>
          
          {/* Access Level Info */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Eye className="w-4 h-4 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Read-Only Access</p>
                <p className="text-xs text-blue-700 mt-1">
                  Reviewers can view all pursuit data including evaluations, 
                  documents, and AI analysis. They cannot make changes or decisions.
                </p>
              </div>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium">
                Reviewer's Email <span className="text-red-500">*</span>
              </Label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="consultant@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={isLoading || success}
                  data-testid="reviewer-email-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                User must already have an account on FedBD
              </p>
            </div>
            
            {/* Optional Message */}
            <div>
              <Label htmlFor="message" className="text-sm font-medium">
                Message (Optional)
              </Label>
              <Textarea
                id="message"
                placeholder="Add a note for the reviewer..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 h-20 resize-none"
                disabled={isLoading || success}
                data-testid="reviewer-message-input"
              />
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {/* Success Message */}
          {success && (
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-700">{success}</p>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isLoading || success}
              data-testid="invite-reviewer-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Inviting...
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Invited
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Reviewer
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
        
        {/* Security Note */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-start gap-2 text-xs text-slate-500">
            <Shield className="w-4 h-4 text-slate-400 mt-0.5" />
            <p>
              Access can be revoked at any time from the pursuit settings. 
              All reviewer actions are logged in the audit trail.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
